import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../lib/api.js';
import { PasskeyCancelled, passkeysSupported, signInWithPasskey } from '../lib/passkeys.js';
import { detectTimezone, useSession } from '../lib/session.js';

/**
 * Sign in / create account.
 *
 * Deliberately quiet. No marketing copy, no feature tour, no "welcome to your
 * wellness journey" — someone opening this is here to do something, not to be
 * sold to. The one piece of framing that earns its place is the privacy note,
 * because "where does my mental health data go" is the question anyone sane
 * asks first.
 */
export function Welcome(): JSX.Element {
  const { capabilities, setUser, refresh } = useSession();
  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const canSignup = capabilities?.signupAllowed ?? false;

  async function submit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result =
        mode === 'signup'
          ? await api.signup({ email, password, displayName, timezone: detectTimezone() })
          : await api.login({ email, password });
      setUser(result.user);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  }

  // Not `usePasskey`. It is an ordinary async click handler, and the `use`
  // prefix made the rules-of-hooks lint read it as a hook called inside a
  // callback — a false positive, but one that would have had to be silenced
  // on every run rather than read.
  async function handlePasskeySignIn(): Promise<void> {
    setPasskeyBusy(true);
    setError(null);
    try {
      await signInWithPasskey();
      await refresh();
    } catch (err) {
      // Dismissing the system sheet is a decision, not a failure. Saying
      // nothing is the right response.
      if (!(err instanceof PasskeyCancelled)) {
        setError(err instanceof Error ? err.message : 'Something went wrong.');
      }
    } finally {
      setPasskeyBusy(false);
    }
  }

  return (
    <main className="app" style={{ paddingBottom: 'var(--space-8)' }}>
      <div className="page-header" style={{ paddingTop: 'var(--space-8)' }}>
        <h1>ADHD &amp; Me</h1>
        <p>Your own space to notice what's happening, and work out what helps.</p>
      </div>

      {/* Offered above the form when signing in, because for anyone who has set
          one up it is the faster path and needs no email typed. Hidden during
          signup — there is no account to hold a passkey yet. */}
      {mode === 'login' && passkeysSupported() && (
        <div className="stack" style={{ marginBottom: 'var(--space-5)' }}>
          <button
            className="btn btn-primary btn-lg btn-block"
            onClick={() => void handlePasskeySignIn()}
            disabled={passkeyBusy}
          >
            {passkeyBusy ? <span className="spinner" /> : 'Sign in with Face ID or Touch ID'}
          </button>
          {/* 14 Oct 2026: on a new computer the browser said the site "may
              require a newer or different kind of device" — the passkey lives
              on the phone that made it. Say so before the button is tried. */}
          <p className="faint" style={{ margin: 0 }}>
            A passkey lives on the device that made it. On a new computer, sign in with your password and add one there from Settings.
          </p>
          <p className="faint" style={{ textAlign: 'center' }}>
            or use your password
          </p>
        </div>
      )}

      <form className="stack" onSubmit={submit}>
        {mode === 'signup' && (
          <div className="field">
            <label htmlFor="name">Your name</label>
            <input
              id="name"
              className="input"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
              autoComplete="nickname"
            />
          </div>
        )}

        <div className="field">
          <label htmlFor="email">Email</label>
          <input
            id="email"
            className="input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            inputMode="email"
          />
        </div>

        <div className="field">
          <label htmlFor="password">Password</label>
          <input
            id="password"
            className="input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={mode === 'signup' ? 12 : 1}
            autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
          />
          {mode === 'signup' && (
            <span className="hint">
              At least 12 characters. Length matters far more than symbols — a phrase you'll
              remember beats something clever you won't.
            </span>
          )}
        </div>

        {error && <div className="banner banner-error">{error}</div>}

        <button className="btn btn-primary btn-lg btn-block" disabled={busy}>
          {busy ? <span className="spinner" /> : mode === 'signup' ? 'Create account' : 'Sign in'}
        </button>
      </form>

      {canSignup && (
        <button
          className="btn btn-ghost btn-block"
          style={{ marginTop: 'var(--space-3)' }}
          onClick={() => {
            setMode(mode === 'login' ? 'signup' : 'login');
            setError(null);
          }}
        >
          {mode === 'login' ? "I don't have an account yet" : 'I already have an account'}
        </button>
      )}

      {/*
        Describes THIS server's actual configuration rather than hedging about
        what might be true elsewhere. The original copy claimed nothing ever
        leaves the server, which stops being true the moment an AI provider is
        configured — and it was being read by the one person that mattered to.
      */}
      <div className="banner stack-sm stack" style={{ marginTop: 'var(--space-6)' }}>
        <span>
          <strong>Where this data lives.</strong> Everything you write is stored on the server
          you're running — this one. Nothing is sold, and nothing is used to train anything.
        </span>

        {capabilities?.reasoning || capabilities?.transcription ? (
          <span>
            The AI features are switched on here, so text and audio they work on is sent to{' '}
            {[
              capabilities.transcription ? capabilities.transcriptionProvider : null,
              capabilities.reasoning ? capabilities.reasoningProvider : null,
            ]
              .filter(Boolean)
              .filter((v, i, a) => a.indexOf(v) === i)
              .join(' and ')}{' '}
            to get a reply — and with each recording, the names of your medications and your recent
            check-in chips, to help it spell. Nothing else leaves this server, and you can turn those
            features off.
          </span>
        ) : (
          <span>
            No AI provider is set up here, so nothing you write leaves this server at all.
            Everything in the app still works.
          </span>
        )}

        <span>
          You choose whether to share anything with your specialist, and you can revoke it at any
          time.
        </span>
      </div>
    </main>
  );
}
